<script setup lang="ts">
import { computed, onMounted, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { PlayIcon } from "@heroicons/vue/solid";
import type { ShortDocument } from "@kekkle/shared/types/documents/short-document/short-document";
import { useShortsStore } from "@/stores/modules/shorts/shorts";
import { useShortUploadStore } from "@/stores/modules/shorts/short-upload";
import { useShortFilterStore } from "@/stores/modules/shorts/shorts-filter";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { useUserStore } from "@/stores/modules/user/user";
import { useShortManagement } from "@/composables/useShortManagement";
import ShortCard from "@/components/shorts/ShortCard.vue";
import ShortUploadCard from "@/components/shorts/ShortUploadCard.vue";
import DownloadAppModal from "@/components/shorts/DownloadAppModal.vue";
import AddShortBottomSheet from "@/components/shorts/AddShortBottomSheet.vue";
import ShortViewerModal from "@/components/shorts/ShortViewerModal.vue";
import KekkleEmptyState from "@/components/ui/KekkleEmptyState.vue";
import KekkleProgressBar from "@/components/ui/KekkleProgressBar.vue";
import SkeletonLoader from "@/components/ui/SkeletonLoader.vue";
import DefaultButton from "@/components/Buttons/DefaultButton.vue";
import InformationText from "@/components/InformationText.vue";

const shortsStore = useShortsStore();
const shortUploadStore = useShortUploadStore();
const shortFilterStore = useShortFilterStore();
const groupsStore = useGroupsStore();
const userStore = useUserStore();
const route = useRoute();
const router = useRouter();
const { locale, t } = useI18n();

const { downloadAppModalOpen, triggerRecordShort, closeDownloadAppModal } =
  useShortManagement();

const groupData = computed(() => groupsStore.groupData);
const activeGroupId = computed(() => userStore.activeGroup);
const shorts = computed(() => shortsStore.shorts);
const loading = computed(() => shortsStore.loading);
const isStale = computed(() => shortsStore.isStale);

const currentGroupUpload = computed(() => shortUploadStore.currentGroupUpload);

const unviewedCount = computed(() => shortsStore.unviewedCount);

const dateFilter = computed(() => shortFilterStore.dateFilter);
const authorFilterUuid = computed(() => shortFilterStore.authorUuid);
const hasActiveFilters = computed(() => shortFilterStore.hasActiveFilters());

const authorFilterName = computed(() => {
  if (!authorFilterUuid.value) return "";
  return (
    groupsStore.activeGroupMembersSorted.find(
      (m) => m.uuid === authorFilterUuid.value,
    )?.name ||
    groupsStore.groupData?.members?.[authorFilterUuid.value]?.name ||
    t("global.unknown_user")
  );
});

function formatDate(date: Date): string {
  return new Intl.DateTimeFormat(locale.value, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

function clearDateFilter(): void {
  shortFilterStore.setDateFilter(null);
  if (route.query.date) {
    const remainingQuery = { ...route.query };
    delete remainingQuery.date;
    router.replace({ name: "Shorts", query: remainingQuery });
  }
}

function clearAuthorFilter(): void {
  shortFilterStore.setAuthorFilter(null);
  if (route.query.author) {
    const remainingQuery = { ...route.query };
    delete remainingQuery.author;
    router.replace({ name: "Shorts", query: remainingQuery });
  }
}

function clearAllFilters(): void {
  shortFilterStore.clearAllFilters();
  if (route.query.date || route.query.author) {
    const remainingQuery = { ...route.query };
    delete remainingQuery.date;
    delete remainingQuery.author;
    router.replace({ name: "Shorts", query: remainingQuery });
  }
}

function openShortViewer(short: ShortDocument) {
  shortsStore.openViewer(short);
}

function watchAll() {
  if (shorts.value.length === 0) return;
  // Start from first unviewed short, or first short if all viewed
  const unviewed = shorts.value.find((s) => !shortsStore.isViewed(s));
  const target = unviewed || shorts.value[0];
  if (target) {
    shortsStore.openViewer(target);
  }
}

function handleRetryUpload(id: string) {
  shortUploadStore.retryUpload(id);
}

function handleDiscardUpload(id: string) {
  shortUploadStore.discardUpload(id);
}

// Load filters from URL on page load
shortFilterStore.loadFiltersFromUrl();

// React to URL query filter changes
watch([() => route.query.date, () => route.query.author], () => {
  shortFilterStore.loadFiltersFromUrl();
});

watch(
  [() => route.query.shortId, () => shortsStore.allShorts],
  ([targetShortId, currentShorts]) => {
    if (targetShortId && currentShorts.length > 0) {
      const found = currentShorts.find((s) => s._id === targetShortId);
      if (found && !shortsStore.viewerOpen) {
        shortsStore.openViewer(found);
        // Consume the deep link so later snapshots don't reopen the viewer after it's closed
        const remainingQuery = { ...route.query };
        delete remainingQuery.shortId;
        router.replace({ name: "Shorts", query: remainingQuery });
      }
    }
  },
  { immediate: true },
);

onMounted(() => {
  if (activeGroupId.value) {
    shortsStore.subscribeToGroup(activeGroupId.value);
  }
  shortUploadStore.restorePendingUploads();
});
</script>

<template>
  <div class="flex h-screen flex-col pb-16">
    <!-- Header -->
    <h1
      class="w-full max-w-xl shrink-0 border-b-2 border-b-kekkle-yellow bg-white py-4 text-center text-xl font-medium dark:bg-kekkle-dark-dark dark:text-slate-200"
    >
      {{ $t("pages.shorts.title_of") }}
      {{ groupData?.name ?? "" }}
    </h1>

    <KekkleProgressBar :active="isStale" color="yellow" />

    <!-- Filter Banners -->
    <div
      v-if="hasActiveFilters"
      class="mx-auto w-full max-w-xl shrink-0 px-3 pt-2"
    >
      <!-- Date Filter Banner (when shorts exist) -->
      <InformationText
        v-if="!loading && dateFilter && shorts.length > 0"
        color="yellow"
        dismissable
        :information="$t('pages.shorts.showing_shorts_from')"
        @dismiss="clearDateFilter"
      >
        {{ formatDate(dateFilter) }}
      </InformationText>

      <!-- Date Filter Banner (empty results) -->
      <InformationText
        v-if="!loading && dateFilter && shorts.length === 0"
        color="yellow"
        dismissable
        class="mx-auto"
        :information="$t('pages.shorts.no_shorts_this_date')"
        @dismiss="clearDateFilter"
      >
        {{ formatDate(dateFilter) }}
      </InformationText>

      <!-- Author Filter Banner (when shorts exist) -->
      <InformationText
        v-if="!loading && authorFilterUuid && shorts.length > 0"
        color="yellow"
        dismissable
        :information="$t('pages.shorts.showing_shorts_by')"
        @dismiss="clearAuthorFilter"
      >
        {{ authorFilterName }}
      </InformationText>

      <!-- Author Filter Banner (empty results) -->
      <InformationText
        v-if="!loading && authorFilterUuid && shorts.length === 0"
        color="yellow"
        dismissable
        class="mx-auto"
        :information="$t('pages.shorts.no_shorts_by_author')"
        @dismiss="clearAuthorFilter"
      >
        {{ authorFilterName }}
      </InformationText>
    </div>

    <!-- Top Action Bar when shorts or upload exist -->
    <div
      v-if="shorts.length > 0 || currentGroupUpload"
      class="flex shrink-0 items-center justify-between px-3 py-2"
    >
      <div class="flex items-center gap-2">
        <span class="text-sm font-bold text-gray-700 dark:text-gray-300">
          {{ shorts.length + (currentGroupUpload ? 1 : 0) }}
          {{
            $t("global.shorts", shorts.length + (currentGroupUpload ? 1 : 0))
          }}
        </span>
        <span
          v-if="unviewedCount > 0"
          class="rounded-full border border-black-dark bg-kekkle-yellow px-2 py-0.5 text-[11px] font-bold text-black-dark shadow-sm"
        >
          {{ unviewedCount }} {{ $t("pages.shorts.unviewed").toLowerCase() }}
        </span>
      </div>

      <div>
        <!-- Watch All Button -->
        <DefaultButton
          v-if="shorts.length > 0"
          color="yellow"
          size="small"
          @click="watchAll"
        >
          <div class="flex items-center gap-1">
            <PlayIcon class="h-4 w-4" />
            <span>{{ $t("pages.shorts.watch_all") }}</span>
          </div>
        </DefaultButton>
      </div>
    </div>

    <!-- Loading Skeleton -->
    <div
      v-if="loading && shorts.length === 0 && !currentGroupUpload"
      class="m-4"
    >
      <SkeletonLoader />
    </div>

    <!-- Empty State (No active filters) -->
    <div
      v-else-if="
        !loading &&
        shorts.length === 0 &&
        !hasActiveFilters &&
        !currentGroupUpload
      "
      class="mx-auto my-auto w-full max-w-md p-4"
    >
      <KekkleEmptyState
        emoji="🎬"
        :title="$t('pages.shorts.no_shorts_yet')"
        :description="$t('pages.shorts.no_shorts_description')"
        :action-text="$t('pages.shorts.record_first')"
        action-color="yellow"
        @action="triggerRecordShort"
      />
    </div>

    <!-- Empty State (With active filters) -->
    <div
      v-else-if="
        !loading &&
        shorts.length === 0 &&
        hasActiveFilters &&
        !currentGroupUpload
      "
      class="mx-auto my-auto flex flex-col items-center justify-center p-4 text-center"
    >
      <DefaultButton color="yellow" size="small" @click="clearAllFilters">
        {{ $t("pages.shorts.clear_filter") }}
      </DefaultButton>
    </div>

    <!-- Group Shorts Grid (Non-infinite scroll) -->
    <div v-else class="min-h-0 flex-1 overflow-y-auto px-3 py-2 pb-28">
      <div class="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <!-- In-progress or Failed Upload Placeholder Card -->
        <ShortUploadCard
          v-if="currentGroupUpload"
          :key="currentGroupUpload.id"
          :upload="currentGroupUpload"
          @retry="handleRetryUpload"
          @discard="handleDiscardUpload"
        />

        <!-- Published Shorts Cards -->
        <ShortCard
          v-for="short in shorts"
          :key="short._id"
          :short="short"
          :group-id="activeGroupId ?? ''"
          @click="openShortViewer"
        />
      </div>
    </div>
  </div>

  <!-- Modals & Sheets -->
  <DownloadAppModal
    :visible="downloadAppModalOpen"
    @close="closeDownloadAppModal"
  />
  <AddShortBottomSheet />
  <ShortViewerModal />
</template>
